import { motion } from "framer-motion";
import { ArrowDown, Play } from "lucide-react";

/* ────────────────────────────────────────────────────────────
   Case studies data, add new entries here in the same shape.
   ──────────────────────────────────────────────────────────── */
export const CASE_STUDIES = [
  {
    slug: "king-highways-emcc",
    eyebrowLabel: "Case Study · 01",
    pillarTag: "SAFETY · INNOVATION · MARKET LAUNCH",
    clientLine1: "KING Highways",
    clientLine2: "EMCC Launch",
    client: "KING Highways",
    cardTitle: "KING Highways EMCC Launch",
    heroSub:
      "Helping KING Highways bring a new safety-led highways solution to market through video, photography and content.",
    cardTagline: "Safety · Innovation · Launch",
    heroVideoLoop: "/videos/king-emcc.mp4",
    heroVideoFull: "/videos/king-emcc-full.mp4",
    heroPoster: "/images/case-studies/king/king-3.jpg",
    thumb: "/images/case-studies/king/king-3.jpg",
    challenge: {
      image: "/images/case-studies/king/king-4.jpg",
      imageAlt:
        "KING Highways EMCC vehicles deployed during rolling block operation",
      badge: "EMCC · ROLLING BLOCK",
      paras: [
        "KING Highways had developed a new Enhanced Mobile Carriageway Closure vehicle designed to improve safety during rolling block operations.",
        "The challenge was to explain the technology clearly, show how it works in a real highways environment and support its launch to the wider traffic management sector.",
      ],
    },
    delivery: {
      image: "/images/case-studies/king/king-1.jpg",
      imageAlt: "KING Highways EMCC vehicle branding detail",
      badge: "ON LOCATION",
      paras: [
        "Highways Industry worked with KING Highways to create a complete content package for the launch.",
        "This included professional photography, video production and editorial content that explained the EMCC vehicle, its safety features and the short-term hire option through King Rental.",
        "The content was created to give KING a clear, professional way to present the vehicle to customers, partners and the wider highways market.",
      ],
      tags: ["Photography", "Video Production", "Editorial Content"],
    },
    result: {
      image: "/images/case-studies/king/king-2.jpg",
      imageAlt:
        "KING Highways EMCC vehicle in service on the motorway network",
      badge: "LIVE ON THE NETWORK",
      paras: [
        "The project gave KING Highways a strong launch package for a new safety-focused solution.",
        "The video, photography and article content helped show the EMCC vehicle in action, explain the technology and position KING as a company bringing practical innovation to the highways sector.",
        "By making the vehicles available through short-term hire, KING has also made the solution easier for traffic management providers to trial and adopt.",
      ],
    },
    cta: {
      eyebrow: "Talk To Highways Industry",
      title1: "Need to launch a new",
      title2: "highways product or service?",
      body: "From product launches and innovation trials to live project delivery, Highways Industry creates the video, photography and content needed to tell your story and reach the right audience.",
      buttonLabel: "Talk To Us",
      buttonLink: "/contact",
    },
  },
  {
    slug: "highway-care-falcon-aclm",
    eyebrowLabel: "Case Study · 02",
    pillarTag: "AUTOMATION · SAFETY · INNOVATION",
    clientLine1: "Highway Care",
    clientLine2: "Falcon ACLM",
    client: "Highway Care",
    cardTitle: "Highway Care Falcon ACLM",
    heroSub:
      "Supporting the launch of automated traffic management technology through content, video and photography.",
    cardTagline: "Automation · Safety · Innovation",
    heroVideoLoop: "/videos/highway-care-aclm.mp4",
    heroVideoFull: "/videos/highway-care-aclm-full.mp4",
    heroPoster: "/images/case-studies/highway-care/cover.jpg",
    thumb: "/images/case-studies/highway-care/aclm-night.png",
    challenge: {
      image: "/images/case-studies/highway-care/cover.jpg",
      imageAlt: "Falcon ACLM laying cones during live motorway operations",
      badge: "ACLM · LIVE OPERATIONS",
      paras: [
        "Highway Care had developed the Falcon Automated Cone Laying Machine, a new approach to traffic management designed to remove operatives from one of the highest-risk activities on the road network.",
        "The challenge was to demonstrate how the technology worked in real-world conditions, explain the safety benefits and build awareness across the highways industry.",
        "As a completely new way of delivering cone laying operations, the technology needed strong visual storytelling that would help customers, contractors and industry stakeholders understand both the innovation and its practical application.",
      ],
    },
    delivery: {
      image: "/images/case-studies/highway-care/aclm-day.png",
      imageAlt:
        "Falcon ACLM at Bruntingthorpe testing, behind-the-scenes",
      badge: "BRUNTINGTHORPE TESTING",
      paras: [
        "Highways Industry worked alongside Highway Care throughout the promotion of the Falcon ACLM.",
        "Our team captured professional photography and video content during testing and demonstration activities at Bruntingthorpe, before documenting the machine in live operation on the M25.",
        "Alongside visual content, we produced industry-focused feature articles and editorial coverage that explained the technology, highlighted its development journey and showcased the safety improvements it delivers for both road workers and road users.",
        "The content was created to help Highway Care communicate the purpose of the ACLM, demonstrate its performance and support wider industry engagement around automation in traffic management.",
      ],
      tags: ["Photography", "Video Production", "Industry Content"],
    },
    result: {
      image: "/images/case-studies/highway-care/aclm-night.png",
      imageAlt:
        "Falcon ACLM operating in a live traffic management environment on the M25",
      badge: "LIVE ON THE M25",
      paras: [
        "The Falcon ACLM became one of the most talked-about innovations in UK traffic management.",
        "Through a combination of video, photography and editorial content, Highway Care was able to demonstrate the technology in action, communicate its safety benefits and engage the wider highways sector.",
        "The content supported industry demonstrations, product awareness and ongoing conversations around reducing risk for traffic management operatives through automation.",
        "By showcasing both testing and live deployment environments, the project helped position Highway Care as a company continuing to develop practical solutions that improve safety on the UK's road network.",
      ],
    },
    cta: {
      eyebrow: "Talk To Highways Industry",
      title1: "Launching new technology",
      title2: "into the highways sector?",
      body: "From product launches and innovation trials to live project delivery, Highways Industry creates the video, photography and content needed to tell your story and reach the right audience.",
      buttonLabel: "Talk To Us",
      buttonLink: "/contact",
    },
  },
  {
    slug: "hds-kier-drone-inspections",
    eyebrowLabel: "Case Study · 03",
    pillarTag: "TECHNOLOGY · INNOVATION · COLLABORATION",
    clientLine1: "Highways Drone Services",
    clientLine2: "& Kier",
    client: "HDS · Kier · Birmingham City Council",
    cardTitle: "Highways Drone Services & Kier",
    heroSub:
      "Showcasing how drone technology changed the future of highways asset inspections.",
    cardTagline: "Technology · Innovation · Collaboration",
    heroVideoLoop: "/videos/hds-kier.mp4",
    heroVideoFull: "/videos/hds-kier-full.mp4",
    heroPoster: "/images/case-studies/hds-kier/hero.jpg",
    thumb: "/images/case-studies/hds-kier/hero.jpg",
    challenge: {
      image: "/images/case-studies/hds-kier/challenge.jpg",
      imageAlt:
        "HDS operator flying a drone to inspect a highway street lighting column",
      badge: "STREET LIGHTING INSPECTION",
      paras: [
        "Kier, working in partnership with Birmingham City Council, wanted to explore a safer and smarter approach to electrical inspections across the highway network.",
        "Traditional inspections relied on operatives working at height and within traffic management environments, creating challenges around safety, disruption and efficiency.",
        "The challenge was to demonstrate how drone technology could move from an initial trial into everyday highways delivery and communicate the benefits clearly to the wider industry.",
      ],
    },
    delivery: {
      image: "/images/case-studies/hds-kier/delivery.png",
      imageAlt:
        "Isometric visualisation of Birmingham with HDS, Kier and Birmingham City Council branding",
      badge: "HDS · KIER · BCC",
      paras: [
        "Highways Industry worked with HDS and Kier to transform the project from an operational innovation into a story the industry could understand.",
        "Through video production, photography, presentations, feature articles and award submissions, we helped demonstrate how drones were changing the way highways inspections are delivered.",
        "The content showcased the technology in action, explained the collaboration between HDS, Kier and Birmingham City Council, and highlighted how digital solutions can support safer, more efficient infrastructure maintenance.",
        "We created content for industry awareness campaigns, award submissions, project presentations, website and social media features, and video case studies.",
      ],
      tags: [
        "Video Production",
        "Photography",
        "Industry Content",
        "Award Support",
      ],
    },
    result: {
      image: "/images/case-studies/hds-kier/result.jpg",
      imageAlt:
        "HDS drone operators observing a drone in flight during a live inspection",
      badge: "AWARD-NOMINATED PROJECT",
      paras: [
        "The project helped demonstrate that drone inspections could become part of everyday highways operations.",
        "Through clear communication, video storytelling and industry content, the partnership gained recognition across the sector and helped change perceptions around the role of drones in infrastructure maintenance.",
        "The collaboration between HDS, Kier and Birmingham City Council went on to receive multiple industry award nominations and recognition, positioning the project as an example of how technology can create safer, smarter ways of working.",
      ],
    },
    cta: {
      eyebrow: "Talk To Highways Industry",
      title1: "Have an innovation the",
      title2: "industry needs to see?",
      body: "Highways Industry helps companies turn projects, technology and success stories into content that reaches the right audience. From video and photography to features, campaigns and award submissions, we help bring your work to life.",
      buttonLabel: "Tell Your Story",
      buttonLink: "/contact",
    },
  },
  {
    slug: "tarmac-silverstone-resurfacing",
    eyebrowLabel: "Case Study · 04",
    pillarTag: "PRECISION · ENGINEERING · ICONIC PROJECTS",
    clientLine1: "Tarmac Silverstone",
    clientLine2: "Resurfacing Project",
    client: "Tarmac · Silverstone Circuit",
    cardTitle: "Tarmac Silverstone Resurfacing Project",
    heroSub:
      "Showcasing precision engineering at one of the world's most famous racing circuits.",
    cardTagline: "Precision · Engineering · Iconic Projects",
    heroVideoLoop: "/videos/tarmac-silverstone.mp4",
    heroVideoFull: "/videos/tarmac-silverstone-full.mp4",
    heroPoster: "/images/case-studies/tarmac-silverstone/hero.png",
    thumb: "/images/case-studies/tarmac-silverstone/thumb.jpg",
    challenge: {
      image: "/images/case-studies/tarmac-silverstone/challenge.png",
      imageAlt:
        "Aerial drone view of Tarmac's resurfacing convoy on the Silverstone start-finish straight",
      badge: "GRAND PRIX SURFACE",
      paras: [
        "Silverstone required a high-performance racing surface capable of meeting the demands of international motorsport.",
        "Tarmac was selected to deliver the specialist resurfacing project, using advanced asphalt technology, digital construction methods and precision paving techniques.",
        "The challenge was to capture the scale, detail and engineering behind the project while documenting the technology and expertise required to resurface an iconic Grand Prix circuit.",
      ],
    },
    delivery: {
      image: "/images/case-studies/tarmac-silverstone/delivery.jpg",
      imageAlt:
        "Top-down view of Tarmac pavers and asphalt trucks working across the Silverstone circuit",
      badge: "PRECISION PAVING",
      paras: [
        "Highways Industry and Highways Drone Services worked with Tarmac to capture the project through professional video, photography and drone technology.",
        "Our team created cinematic visuals showing the resurfacing process, specialist equipment and the people delivering the project.",
        "Drone technology was also used to provide valuable aerial data, helping review the circuit surface and identify areas requiring attention during the programme.",
        "The content captured drone surveys and aerial mapping, cinematic project filming, professional photography, construction progress footage, specialist surfacing operations, and digital content for internal and external communications.",
      ],
      tags: [
        "Cinematic Video Production",
        "Drone Surveys",
        "Photography",
        "Industry Content",
      ],
    },
    result: {
      image: "/images/case-studies/tarmac-silverstone/result.png",
      imageAlt:
        "Sweeping aerial view of Tarmac laying the finished asphalt through a Silverstone corner",
      badge: "READY FOR RACE WEEKEND",
      paras: [
        "The project created a complete visual record of one of the most prestigious resurfacing schemes in the UK.",
        "Through cinematic video, aerial imagery and digital content, Tarmac was able to showcase the expertise, technology and precision behind delivering a racing surface for the highest level of motorsport.",
        "The content helped communicate the story behind the project, from advanced construction methods through to the finished Silverstone Grand Prix circuit.",
      ],
    },
    cta: {
      eyebrow: "Talk To Highways Industry",
      title1: "Delivering a project",
      title2: "the industry should see?",
      body: "Highways Industry helps companies capture major infrastructure projects through video, photography, drone technology and storytelling. From construction milestones to completed schemes, we create content that demonstrates the people, technology and expertise behind your work.",
      buttonLabel: "Showcase Your Project",
      buttonLink: "/contact",
    },
  },
  {
    slug: "rc-williams",
    eyebrowLabel: "Case Study · 05",
    pillarTag: "BRAND · CONSISTENCY · STORYTELLING",
    clientLine1: "R&C Williams",
    clientLine2: "10 Years of Brand Building",
    client: "R&C Williams",
    cardTitle: "R&C Williams",
    heroSub:
      "10 years of building a stronger highways brand through content, creativity and consistency.",
    cardTagline: "Brand · Consistency · Storytelling",
    heroVideoLoop: "/videos/rc-williams.mp4",
    heroVideoFull: "/videos/rc-williams-full.mp4",
    heroPoster: "/images/case-studies/rc-williams/hero-mosaic.png",
    thumb: "/images/case-studies/rc-williams/hero-mosaic.png",
    challenge: {
      image: "/images/case-studies/rc-williams/challenge.jpg",
      imageAlt:
        "R&C Williams site team reviewing project plans on site, showing 60-years branding",
      badge: "10-YEAR PARTNERSHIP",
      paras: [
        "R&C Williams had built a strong reputation through the quality of their work but did not have a dedicated internal marketing team to consistently share their story.",
        "Like many successful highways businesses, their projects, people, achievements and community work were happening every day but were not always being captured or promoted.",
        "The challenge was to create a consistent brand presence that reflected the company, showcased their expertise and helped more people across the industry understand who they are and what they deliver.",
      ],
    },
    delivery: {
      image: "/images/case-studies/rc-williams/delivery.jpg",
      imageAlt:
        "Behind-the-scenes of an R&C Williams operative rigging a lift on site",
      badge: "BRAND-AS-A-SERVICE",
      paras: [
        "Highways Industry became an extension of the R&C Williams team, managing their marketing channels and building a consistent approach to content.",
        "Working together for almost 10 years, we have helped shape their brand identity through photography, video production, social media management, campaigns and industry features.",
        "Our focus has been on telling real stories, showcasing major projects, the people behind the business, social value, community work, charity initiatives, awards and achievements, company milestones and industry success stories.",
        "By creating regular, authentic content, we helped move their online presence from occasional updates into a consistent brand platform.",
      ],
      tags: [
        "Social Media Management",
        "Video Production",
        "Photography",
        "Brand Development",
        "Content Campaigns",
      ],
    },
    result: {
      image: "/images/case-studies/rc-williams/result.jpg",
      imageAlt:
        "Aerial view of R&C Williams fleet on a completed highways project",
      badge: "STRONGER BRAND · WIDER REACH",
      paras: [
        "R&C Williams has developed a stronger digital presence and a more recognised brand across the highways and infrastructure sector.",
        "Through organic content alone, their audience and engagement has continued to grow without relying on paid advertising, LinkedIn audience growth from around 200 followers to almost 2,000, over 1,000,000 content views generated, video views and watch time increased significantly, and over 70% of views reaching new audiences.",
        "Project photography drove the majority of engagement, and the partnership demonstrates the value of consistent storytelling, showing the people, projects and culture behind a business.",
      ],
    },
    cta: {
      eyebrow: "Talk To Highways Industry",
      title1: "Your business already has a story.",
      title2: "Are you showing it?",
      body: "Highways Industry helps highways companies build stronger brands by consistently showcasing their projects, people and achievements. From content creation and social media management to video, photography and campaigns, we help the industry see the work you deliver.",
      buttonLabel: "Build Your Brand",
      buttonLink: "/contact",
    },
  },
  {
    slug: "bt-hs-electric-ipv",
    eyebrowLabel: "Case Study · 06",
    pillarTag: "SUSTAINABILITY · INNOVATION · TRAFFIC MANAGEMENT",
    clientLine1: "BT-HS",
    clientLine2: "Electric IPV",
    client: "BT-HS",
    cardTitle: "BT-HS Electric IPV",
    heroSub:
      "Helping BT-HS lead the conversation around sustainable traffic management.",
    cardTagline: "Sustainability · Innovation · Fleet Transition",
    heroVideoLoop: "/videos/bt-hs-electric-ipv.mp4",
    heroVideoFull: "/videos/bt-hs-electric-ipv-full.mp4",
    heroPoster: "/images/case-studies/bt-hs-electric-ipv/hero.png",
    thumb: "/images/case-studies/bt-hs-electric-ipv/hero.png",
    challenge: {
      image: "/images/case-studies/bt-hs-electric-ipv/challenge.png",
      imageAlt:
        "BT-HS Electric IPV operating on a live UK road, first-of-its-kind sustainable traffic management vehicle",
      badge: "FIRST-OF-ITS-KIND E-IPV",
      paras: [
        "BT-HS developed one of the first electric Impact Protection Vehicles for the traffic management sector.",
        "The challenge was to help the industry understand the vehicle, its purpose and its role in the move toward lower-carbon highways operations.",
        "As a new concept in the market, BT-HS needed clear, consistent content that explained the E-IPV, addressed industry questions and positioned the company as a leader in sustainable traffic management innovation.",
      ],
    },
    delivery: {
      image: "/images/case-studies/bt-hs-electric-ipv/delivery.png",
      imageAlt:
        "Close-up of a BT-HS operative plugging the Electric IPV into an EV fast-charger",
      badge: "LIFT CAMPAIGN",
      paras: [
        "Highways Industry worked with BT-HS across video, photography, web development, branding and ongoing campaign content.",
        "Through our LIFT campaign support, we helped BT-HS create regular content around the E-IPV, electric fleet adoption, battery safety, grant support and the wider shift toward zero-emission highways vehicles.",
        "Our work helped BT-HS communicate the E-IPV clearly across its digital channels and wider industry media, social media management, LIFT campaign content, video production, photography, website development, brand development, product launch content, and industry articles and features.",
      ],
      tags: [
        "LIFT Campaign",
        "Social Media Management",
        "Video Production",
        "Photography",
        "Web Development",
        "Brand Support",
        "Industry Content",
      ],
    },
    result: {
      image: "/images/case-studies/bt-hs-electric-ipv/hero.png",
      imageAlt:
        "BT-HS Electric IPV on the road, leading the conversation on zero-emission traffic management",
      badge: "LEADING THE E-IPV CONVERSATION",
      paras: [
        "The campaign helped BT-HS build awareness around the E-IPV and strengthen its position as a forward-thinking vehicle manufacturer in the highways sector.",
        "By creating regular content around sustainability, safety and practical fleet transition, BT-HS was able to lead the conversation around electric IPVs and greener traffic management vehicles.",
        "The content gave the business a clear voice, helped answer industry concerns and supported the launch of a product designed to move the sector toward a cleaner future.",
      ],
    },
    cta: {
      eyebrow: "Talk To Highways Industry",
      title1: "Launching a sustainable",
      title2: "highways product?",
      body: "Highways Industry helps highways businesses turn new products, campaigns and ideas into content the sector understands. From LIFT campaigns and social media to video, photography, websites and brand support, we help your business stay visible.",
      buttonLabel: "Start Your Campaign",
      buttonLink: "/contact",
    },
  },
  {
    slug: "eurovia-hackathon",
    eyebrowLabel: "Case Study · 07",
    pillarTag: "PEOPLE · CULTURE · INNOVATION",
    clientLine1: "Eurovia",
    clientLine2: "Hackathon",
    client: "Eurovia",
    cardTitle: "Eurovia Hackathon & Internal Engagement Campaign",
    heroSub:
      "Capturing the people, ideas and conversations shaping the future of the business.",
    cardTagline: "People · Culture · Internal Engagement",
    heroVideoLoop: "/videos/eurovia-hackathon.mp4",
    heroVideoFull: "/videos/eurovia-hackathon-full.mp4",
    heroPoster: "/images/case-studies/eurovia-hackathon/hero.png",
    thumb: "/images/case-studies/eurovia-hackathon/hero.png",
    challenge: {
      image: "/images/case-studies/eurovia-hackathon/challenge.png",
      imageAlt:
        "Eurovia employee interview in front of the Hackathon 2017 branded backdrop",
      badge: "HACKATHON · TEAM COLLABORATION",
      paras: [
        "Eurovia wanted to bring people from across the business together to explore ideas, identify challenges and create new solutions.",
        "The Hackathon was designed to encourage collaboration between teams, giving employees from different areas of the company the opportunity to share their experiences and contribute ideas for the future.",
        "The challenge was to capture the energy of the event, communicate the purpose behind it and create content that reflected Eurovia's commitment to listening to its people.",
      ],
    },
    delivery: {
      image: "/images/case-studies/eurovia-hackathon/delivery.png",
      imageAlt:
        "Eurovia leadership presenting to Hackathon attendees during a workshop session",
      badge: "EVENT FILMING · INTERVIEWS",
      paras: [
        "Highways Industry worked with Eurovia to capture the Hackathon through professional video, photography and storytelling.",
        "Our team documented the event, including leadership messages, team activities, employee discussions and the ideas being created throughout the day.",
        "The content focused on the people behind the business, showing collaboration, problem solving and how different teams could work together to shape future improvements.",
        "We supported Eurovia through event filming, employee interviews, leadership communication videos, photography, internal campaign content and brand storytelling.",
      ],
      tags: [
        "Event Video Production",
        "Photography",
        "Internal Communications",
        "Leadership Content",
        "Campaign Support",
      ],
    },
    result: {
      image: "/images/case-studies/eurovia-hackathon/hero.png",
      imageAlt:
        "Eurovia Hackathon 2017, The vision beyond 2020 event branding",
      badge: "PEOPLE-LED CULTURE",
      paras: [
        "The campaign provided Eurovia with a powerful way to showcase its people, culture and approach to innovation.",
        "The final content captured the importance of collaboration and gave employees a voice in the future direction of the business.",
        "By turning an internal event into professional digital content, Eurovia was able to share the ideas, energy and outcomes from the Hackathon across the wider organisation.",
      ],
    },
    cta: {
      eyebrow: "Talk To Highways Industry",
      title1: "Your people are",
      title2: "your biggest story.",
      body: "Highways Industry helps companies capture the moments that define their culture. From internal campaigns and events to project launches and industry stories, we create content that connects people with your message.",
      buttonLabel: "Capture Your Story",
      buttonLink: "/contact",
    },
  },
  {
    slug: "wj-group",
    eyebrowLabel: "Case Study · 08",
    pillarTag: "ROAD MARKING · INNOVATION · LONG-TERM PARTNERSHIP",
    clientLine1: "WJ Group",
    clientLine2: "10+ Years of Storytelling",
    client: "WJ Group",
    cardTitle: "WJ Group",
    heroSub:
      "Supporting an industry leader with over 10 years of highways storytelling.",
    cardTagline: "Road Marking · Innovation · Long-Term Partnership",
    heroVideoLoop: "/videos/wj-group.mp4",
    heroVideoFull: "/videos/wj-group-full.mp4",
    heroPoster: "/images/case-studies/wj-group/hero.png",
    thumb: "/images/case-studies/wj-group/hero.png",
    challenge: {
      image: "/images/case-studies/wj-group/challenge.png",
      imageAlt:
        "Close-up of the WJ Group brand on a specialist road-marking vehicle with the WJ SafeSite QR code",
      badge: "ROAD MARKING LEADER",
      paras: [
        "WJ has grown to become one of the most recognised names in road marking and specialist highways services.",
        "As the company continued to develop, invest and innovate, it needed a platform to consistently share its projects, people, technology and achievements with the wider highways sector.",
        "The challenge was to make sure those stories reached the right audience and supported WJ's position as a leader within the industry.",
      ],
    },
    delivery: {
      image: "/images/case-studies/wj-group/hero.png",
      imageAlt:
        "WJ line-painting truck and operative delivering a night-time road marking operation",
      badge: "PARTNERSHIP SINCE 2014",
      paras: [
        "Highways Industry has worked alongside WJ since our launch in 2014, supporting their brand visibility and helping showcase their growth.",
        "From supporting the communication around their new brand identity over a decade ago, through to ongoing project features and industry updates, we have helped tell the WJ story.",
        "Over this partnership, Highways Industry has created and shared almost 200 features covering innovation, safety improvements, major projects, new technology, sustainability, company milestones, people and culture, and industry achievements.",
        "The content has helped keep WJ visible across the highways sector and connected with the audiences that matter.",
      ],
      tags: [
        "Industry Features",
        "Brand Visibility",
        "Social Media Reach",
        "Content Campaigns",
        "Long-Term Partnership",
      ],
    },
    result: {
      image: "/images/case-studies/wj-group/result.png",
      imageAlt:
        "Macro close-up of the high-friction glass beads that give WJ road markings their retroreflective performance",
      badge: "10+ YEARS · 200 FEATURES",
      paras: [
        "The long-term partnership has helped establish a consistent digital presence for one of the sector's leading specialist contractors.",
        "Through Highways Industry's platform and social channels, WJ content has generated almost 200 industry features published, over 100,000 article views, over 60,000 social media views, and continuous industry exposure since 2014.",
        "The partnership demonstrates the impact of consistent storytelling and the value of documenting a company's journey over time.",
        "From brand development to becoming a recognised leader in road marking innovation, WJ's story has been shared with the industry every step of the way.",
      ],
    },
    cta: {
      eyebrow: "Talk To Highways Industry",
      title1: "Become a recognised",
      title2: "name in your industry.",
      body: "Building a strong brand does not happen from a single post or campaign. Highways Industry works with companies over the long term, consistently sharing the projects, people and achievements that make them stand out. From growing businesses to established industry leaders, we help make sure your story is seen.",
      buttonLabel: "Build Your Industry Presence",
      buttonLink: "/contact",
    },
  },
];

/* ────────────────────────────────────────────────────────────
   Reusable case-study section (Hero + 01 / 02 / 03 blocks)
   ──────────────────────────────────────────────────────────── */
export function CaseStudySection({ cs, onWatchFilm }) {
  const projectId = `cs-${cs.slug}-project`;
  const scrollToProject = (e) => {
    e.preventDefault();
    const el = document.getElementById(projectId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section data-testid={`case-study-${cs.slug}`}>
      {/* Hero */}
      <div
        data-testid={`${cs.slug}-hero`}
        className="dark-section relative min-h-[92vh] flex flex-col overflow-hidden bg-black"
      >
        {cs.heroVideoLoop ? (
          <video
            key={`${cs.slug}-loop`}
            data-testid={`${cs.slug}-hero-video`}
            autoPlay
            muted
            loop
            playsInline
            poster={cs.heroPoster || undefined}
            className="absolute inset-0 w-full h-full object-cover opacity-75"
          >
            <source src={cs.heroVideoLoop} type="video/mp4" />
          </video>
        ) : cs.heroPoster ? (
          <img
            key={`${cs.slug}-poster`}
            src={cs.heroPoster}
            alt={cs.cardTitle}
            data-testid={`${cs.slug}-hero-poster`}
            className="absolute inset-0 w-full h-full object-cover opacity-75"
          />
        ) : (
          <div
            data-testid={`${cs.slug}-hero-placeholder`}
            className="absolute inset-0 w-full h-full bg-gradient-to-br from-slate-900 via-zinc-950 to-black"
          >
            <div className="absolute inset-0 opacity-40 blueprint-dots" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />

        <div className="relative z-10 w-full px-5 md:px-10 pt-28 md:pt-32">
          <div className="mx-auto max-w-[1400px] flex items-start justify-between gap-6">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                {cs.eyebrowLabel}
              </span>
            </div>
            <span className="hidden md:inline-block font-mono text-[10px] text-accent-blue tracking-widest">
              {cs.pillarTag}
            </span>
          </div>
        </div>

        <div className="relative z-10 w-full px-5 md:px-10 pb-20 lg:pb-24 mt-auto">
          <div className="mx-auto max-w-[1400px]">
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="font-heading font-black text-5xl md:text-7xl lg:text-[7rem] text-white leading-[0.95] tracking-tighter max-w-5xl"
            >
              {cs.clientLine1}
              <br />
              <span className="text-accent-blue italic">{cs.clientLine2}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
              className="mt-8 text-lg md:text-xl text-zinc-200 max-w-2xl leading-relaxed"
            >
              {cs.heroSub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              {cs.heroVideoFull && (
                <button
                  type="button"
                  onClick={onWatchFilm}
                  data-testid={`${cs.slug}-watch-film`}
                  className="group inline-flex items-center gap-3 bg-primary-blue text-white px-7 py-4 text-sm md:text-base font-bold uppercase tracking-widest hover:glow-blue transition-all rounded-full"
                >
                  <Play className="h-4 w-4" /> Watch The Film
                </button>
              )}
              <a
                href={`#${projectId}`}
                onClick={scrollToProject}
                data-testid={`${cs.slug}-view-project`}
                className={`group inline-flex items-center gap-3 px-5 py-4 text-sm md:text-base font-bold uppercase tracking-widest transition-colors rounded-full ${
                  cs.heroVideoFull
                    ? "text-white/90 hover:text-accent-blue border border-white/30 hover:border-accent-blue"
                    : "bg-primary-blue text-white hover:glow-blue"
                }`}
              >
                View Project
                <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 01 The Challenge, text left, image right */}
      <div
        id={projectId}
        data-testid={`${cs.slug}-challenge`}
        className="py-20 md:py-28"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <SectionHeading number="01" title="The Challenge" />
            {cs.challenge.paras.map((p, i) => (
              <p
                key={i}
                className="mt-5 first:mt-8 text-lg text-slate-700 leading-relaxed"
              >
                {p}
              </p>
            ))}
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <FramedImage
              src={cs.challenge.image}
              alt={cs.challenge.imageAlt}
              badge={cs.challenge.badge}
              testId={`${cs.slug}-challenge-image`}
            />
          </div>
        </div>
      </div>

      {/* 02 What We Did, image left, text right */}
      <div
        data-testid={`${cs.slug}-delivery`}
        className="py-20 md:py-28 bg-slate-50 border-y border-slate-200"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <FramedImage
              src={cs.delivery.image}
              alt={cs.delivery.imageAlt}
              badge={cs.delivery.badge}
              testId={`${cs.slug}-delivery-image`}
            />
          </div>
          <div className="lg:col-span-6">
            <SectionHeading number="02" title="What We Did" />
            {cs.delivery.paras.map((p, i) => (
              <p
                key={i}
                className="mt-5 first:mt-8 text-lg text-slate-700 leading-relaxed"
              >
                {p}
              </p>
            ))}
            <div className="mt-8 flex flex-wrap gap-2">
              {cs.delivery.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs font-bold uppercase tracking-widest text-slate-700 border border-slate-300 px-3 py-1.5 rounded-full bg-white"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 03 The Result, text left, image right */}
      <div
        data-testid={`${cs.slug}-result`}
        className="py-20 md:py-28"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <SectionHeading number="03" title="The Result" />
            {cs.result.paras.map((p, i) => (
              <p
                key={i}
                className="mt-5 first:mt-8 text-lg text-slate-700 leading-relaxed"
              >
                {p}
              </p>
            ))}
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <FramedImage
              src={cs.result.image}
              alt={cs.result.imageAlt}
              badge={cs.result.badge}
              testId={`${cs.slug}-result-image`}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHeading({ number, title }) {
  return (
    <>
      <div className="flex items-center gap-3">
        <span className="h-px w-12 bg-accent-blue" />
        <span className="label-eyebrow text-accent-blue">Section {number}</span>
      </div>
      <h2 className="font-heading font-black text-4xl md:text-6xl mt-5 leading-[0.95] tracking-tighter text-slate-900">
        {title}
      </h2>
    </>
  );
}

function FramedImage({ src, alt, badge, testId }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 shadow-xl">
      {src ? (
        <img
          src={src}
          alt={alt}
          className="w-full aspect-[4/3] object-cover"
          data-testid={testId}
        />
      ) : (
        <div
          data-testid={testId}
          className="w-full aspect-[4/3] bg-gradient-to-br from-slate-800 via-slate-900 to-black flex flex-col items-center justify-center relative overflow-hidden"
          role="img"
          aria-label={alt}
        >
          <div className="absolute inset-0 opacity-20 blueprint-dots" />
          <div className="relative z-10 flex flex-col items-center gap-3 text-center px-6">
            <div className="h-14 w-14 rounded-full border-2 border-accent-blue/60 flex items-center justify-center">
              <span className="h-2 w-2 rounded-full bg-accent-blue animate-pulse" />
            </div>
            <p className="font-mono text-[10px] tracking-widest text-accent-blue">
              PHOTOGRAPHY IN PROGRESS
            </p>
            <p className="text-sm text-zinc-300 max-w-xs leading-relaxed">
              {alt}
            </p>
          </div>
        </div>
      )}
      {badge && (
        <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-black/70 backdrop-blur-md text-white px-3 py-1.5 text-[10px] font-mono tracking-widest rounded-full">
          <span className="h-1.5 w-1.5 bg-accent-blue rounded-full" />
          {badge}
        </div>
      )}
    </div>
  );
}
